/**
 * plugins/meta-field.ts — Campos da capa (span dentro de .meta)
 *
 * Ferramentas: adicionar campo (label+valor), mover ↑↓ o par, excluir par.
 */

import { VtbPlugin, IVtbRegistry, SmartCtx, closestInside } from '../types';

export class MetaFieldPlugin implements VtbPlugin {
    readonly panelId = 'vtb-meta';
    readonly selectorHints = ['.meta > span', '.meta span', '[data-vtb-id="meta-label"]', '[data-vtb-id="meta-value"]'];
    readonly contextAffinity = ['vtb-cover', 'vtb-doc-header'];

    detect(target: HTMLElement, visualEditor: HTMLElement): SmartCtx | null {
        // Busca span de label ou valor dentro de um container .meta
        const container = closestInside(target, visualEditor, e => 
            e.classList.contains('meta') || e.dataset.vtbId === 'meta'
        );
        
        if (container) {
            // O item pode ser o próprio target se for um span
            let el: HTMLElement | null = target;
            while (el && el !== container) {
                if (el.tagName === 'SPAN' || el.dataset.vtbId === 'meta-label' || el.dataset.vtbId === 'meta-value') {
                    return { type: this.panelId, label: 'Campo Meta', el, container };
                }
                el = el.parentElement;
            }
        }
        return null;
    }

    activate(_ctx: SmartCtx, registry: IVtbRegistry): void {
        const f = registry.q<HTMLElement>('vtb-meta-add-form');
        if (f) f.style.display = 'none';
    }

    setup(registry: IVtbRegistry): void {
        registry.q('vtb-meta-add-open')?.addEventListener('click', () => {
            const f = registry.q<HTMLElement>('vtb-meta-add-form');
            if (f) f.style.display = f.style.display === 'none' ? 'flex' : 'none';
        });
        registry.q('vtb-meta-confirm')?.addEventListener('click', () => this._add(registry));
        registry.q('vtb-meta-cancel')?.addEventListener('click', () => {
            const f = registry.q<HTMLElement>('vtb-meta-add-form');
            if (f) f.style.display = 'none';
        });
        registry.q('vtb-meta-up')?.addEventListener('click',   () => this._move(registry, -1));
        registry.q('vtb-meta-down')?.addEventListener('click', () => this._move(registry,  1));
        registry.q('vtb-meta-del')?.addEventListener('click',  () => this._delete(registry));
    }

    // ── Helpers ───────────────────────────────────────────────────────────────

    private _pair(el: HTMLElement): { label: HTMLElement | null; value: HTMLElement | null } {
        if (el.classList.contains('label')) {
            return { label: el, value: el.nextElementSibling as HTMLElement | null };
        }
        return { label: el.previousElementSibling as HTMLElement | null, value: el };
    }

    private _add(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx || !ctx.container) return;

        const container  = ctx.container;
        const pair       = this._pair(ctx.el);
        const afterEl    = pair.value ?? pair.label ?? ctx.el;
        const labelText  = registry.q<HTMLInputElement>('vtb-meta-label')?.value?.trim() || 'Campo';
        const valueText  = registry.q<HTMLInputElement>('vtb-meta-value')?.value?.trim() || '';

        const labelSpan  = Object.assign(document.createElement('span'), { className: 'label', textContent: labelText });
        const valueSpan  = Object.assign(document.createElement('span'), { textContent: valueText });

        if (afterEl.nextSibling) {
            container.insertBefore(labelSpan, afterEl.nextSibling);
            container.insertBefore(valueSpan, labelSpan.nextSibling!);
        } else {
            container.appendChild(labelSpan);
            container.appendChild(valueSpan);
        }

        const f  = registry.q<HTMLElement>('vtb-meta-add-form');
        if (f) f.style.display = 'none';
        const li = registry.q<HTMLInputElement>('vtb-meta-label');
        const vi = registry.q<HTMLInputElement>('vtb-meta-value');
        if (li) li.value = '';
        if (vi) vi.value = '';

        registry.sync();
        registry.selectEl(valueSpan);
    }

    private _delete(registry: IVtbRegistry): void {
        const ctx = registry.activeCtx;
        if (!ctx) return;
        const { label, value } = this._pair(ctx.el);
        label?.remove();
        value?.remove();
        registry.clearSelection();
        registry.sync();
    }

    private _move(registry: IVtbRegistry, dir: -1 | 1): void {
        const ctx = registry.activeCtx;
        if (!ctx || !ctx.container) return;

        const container = ctx.container;
        const { label, value } = this._pair(ctx.el);
        if (!label || !value) return;

        const spans    = Array.from(container.children) as HTMLElement[];
        const labelIdx = spans.indexOf(label);

        if (dir === -1) {
            if (labelIdx < 2) return;
            const prevLabel = spans[labelIdx - 2];
            container.insertBefore(label, prevLabel);
            container.insertBefore(value, label.nextSibling!);
        } else {
            if (labelIdx + 3 > spans.length) return;
            const nextLabel = spans[labelIdx + 2];
            const nextValue = spans[labelIdx + 3];
            if (!nextLabel) return;
            container.insertBefore(nextLabel, label);
            if (nextValue) container.insertBefore(nextValue, label);
        }

        registry.sync();
    }
}
